import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryCreator } from "redux/baseQuery";
import type {
  CreateUserRequest,
  GetOrganizationRequest,
  GetOrganizationResponse,
  GetOrganizationsRequest,
  GetOrganizationsResponse,
  GetProfileResponse,
  GetUserRequest,
  GetUsersByOrganisationRequest,
  GetUsersByOrganisationResponse,
  GetUsersRequest,
  GetUsersResponse,
  UpdateOrganisationRequest,
  UpdateProfileRequest,
} from "redux/services/membership/types";
import env from "env";
const { membershipService } = env.api.baseUrl;

const baseQuery = fetchBaseQuery({
  baseUrl: membershipService,
  prepareHeaders: (headers, { getState }: any) => {
    const token = getState().auth.accessToken;
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

export const membershipApi = createApi({
  reducerPath: "membershipApi",
  baseQuery: baseQueryCreator(baseQuery),
  endpoints: (builder) => ({
    getProfile: builder.query<GetProfileResponse, void>({
      query: () => "users/me",
    }),
    getOrganization: builder.query<
      GetOrganizationResponse,
      GetOrganizationRequest
    >({
      query: ({ orgId, ...params }) => ({
        url: `organisations/${orgId}`,
        params: {
          ...params,
        },
      }),
    }),
    updateProfile: builder.mutation<GetProfileResponse, UpdateProfileRequest>({
      query: ({ userId, ...patch }) => ({
        url: `users/${userId}`,
        method: "PATCH",
        body: patch,
      }),
    }),
    getUsers: builder.query<
      GetUsersResponse,
      GetUsersRequest & {
        orgId?: string;
      }
    >({
      query: (params) => ({
        url: "/admin/users",
        params: {
          ...params,
        },
      }),
    }),
    getOrganizations: builder.query<
      GetOrganizationsResponse,
      GetOrganizationsRequest
    >({
      query: (params) => ({
        url: "/admin/organisations",
        params: {
          ...params,
        },
      }),
    }),
    getUsersByOrganisation: builder.query<
      GetUsersByOrganisationResponse,
      GetUsersByOrganisationRequest
    >({
      query: ({ orgId, ...params }) => ({
        url: `organisations/${orgId}/users`,
        params: {
          ...params,
        },
      }),
    }),
    getUser: builder.mutation<GetProfileResponse, GetUserRequest>({
      query: (params) => ({
        url: `users/${params.userId}`,
      }),
    }),
    updateOrganisation: builder.mutation<{}, UpdateOrganisationRequest>({
      query: ({ userId, orgId, ...patch }) => ({
        url: `organisations/${orgId}?userId=${userId}`,
        method: "PATCH",
        body: patch,
      }),
    }),
    createUser: builder.mutation<{}, CreateUserRequest>({
      query: ({ ...body }) => ({
        url: `admin/users`,
        method: "POST",
        body: {
          ...body,
          appId: env.app.appName,
          accountType: "FRONT-END",
          listCustomFields: [
            {
              userId: "b7b794b4-204f-462f-aa7a-86646d95cd58",
              customKey: "emailVerified",
              customValue: "true",
            },
          ],
        },
      }),
    }),
  }),
});

export const {
  useGetProfileQuery,
  useGetOrganizationQuery,
  useUpdateProfileMutation,
  useGetUsersQuery,
  useGetUserMutation,
  useUpdateOrganisationMutation,
  useGetUsersByOrganisationQuery,
  useGetOrganizationsQuery,
  useCreateUserMutation,
} = membershipApi;

export const {
  useLazyQuery: useGetProfileLazyQuery,
  useQueryState: useGetProfileQueryState,
} = membershipApi.endpoints.getProfile;

export const {
  useLazyQuery: useGetOrganizationLazyQuery,
  useQueryState: useGetOrganizationQueryState,
} = membershipApi.endpoints.getOrganization;

export const {
  useLazyQuery: useGetUsersLazyQuery,
  useQueryState: useGetUsersQueryState,
} = membershipApi.endpoints.getUsers;
